import React, { useState, useEffect } from 'react'
import { X, ShoppingCart, DollarSign, Loader2, TrendingUp } from 'lucide-react'

interface SoldSlideoutProps {
  isOpen: boolean
  onClose: () => void
  itemData?: {
    id: string
    name: string
    ic: string
    targetPrice: number
    cost: number
  }
  onSaleComplete: (data: any) => void
}

export default function SoldSlideout({
  isOpen,
  onClose,
  itemData,
  onSaleComplete
}: SoldSlideoutProps) {
  const [formData, setFormData] = useState({
    marketplace: '',
    soldFor: '',
    marketplaceFees: '',
    shippingCost: '',
    cogs: itemData?.cost?.toString() || '',
    quantitySold: '1',
    saleDate: new Date().toISOString().split('T')[0],
    notes: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when slideout opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        marketplace: '',
        soldFor: '',
        marketplaceFees: '',
        shippingCost: '',
        cogs: itemData?.cost?.toString() || '',
        quantitySold: '1',
        saleDate: new Date().toISOString().split('T')[0],
        notes: ''
      })
      setIsSubmitting(false)
    }
  }, [isOpen, itemData?.cost])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const calculateNetProfit = () => {
    const soldFor = parseFloat(formData.soldFor) || 0
    const marketplaceFees = parseFloat(formData.marketplaceFees) || 0
    const shippingCost = parseFloat(formData.shippingCost) || 0
    const cogs = parseFloat(formData.cogs) || 0
    return soldFor - marketplaceFees - shippingCost - cogs
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const saleData = {
        item_id: itemData?.id,
        marketplace: formData.marketplace,
        sale_price: parseFloat(formData.soldFor),
        marketplace_fees: parseFloat(formData.marketplaceFees) || 0,
        shipping_fees: parseFloat(formData.shippingCost) || 0,
        quantity_sold: parseInt(formData.quantitySold),
        sale_date: formData.saleDate,
        notes: formData.notes
      }

      const response = await fetch('/api/central-optimized?op=mark-sold', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saleData)
      })

      if (!response.ok) {
        throw new Error('Failed to mark item as sold')
      }

      const result = await response.json()
      if (result.success) {
        onSaleComplete(result)
        onClose()
      } else {
        throw new Error(result.error || 'Failed to mark item as sold')
      }
    } catch (error) {
      console.error('Sale failed:', error)
      alert(`Failed to mark item as sold: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      overflow: 'hidden'
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Slide-out panel */}
      <div style={{
        position: 'absolute',
        right: 0,
        top: 0,
        height: '100%',
        width: '480px',
        background: 'linear-gradient(to bottom, #0a0e1a, #1a1f2e)',
        borderLeft: '2px solid rgba(34, 197, 94, 0.5)',
        boxShadow: '-4px 0 40px rgba(34, 197, 94, 0.3), inset 0 0 80px rgba(34, 197, 94, 0.05)',
        transform: 'translateX(0)',
        transition: 'transform 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid rgba(34, 197, 94, 0.2)',
          background: 'linear-gradient(to right, rgba(34, 197, 94, 0.1), transparent)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(to right, transparent, rgba(34, 197, 94, 0.8), transparent)'
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '10px',
              borderRadius: '8px',
              background: 'rgba(34, 197, 94, 0.2)',
              border: '1px solid rgba(34, 197, 94, 0.4)',
              boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)'
            }}>
              <ShoppingCart style={{ width: '24px', height: '24px', color: '#22C55E' }} />
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#E0E6F0',
              fontFamily: 'Rajdhani, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              textShadow: '0 0 20px rgba(34, 197, 94, 0.5)'
            }}>
              Mark as Sold
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              padding: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <X style={{ width: '20px', height: '20px', color: '#EF4444' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Current Item Section */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05), rgba(16, 185, 129, 0.05))',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle, rgba(34, 197, 94, 0.1), transparent)',
              pointerEvents: 'none'
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <ShoppingCart style={{ width: '20px', height: '20px', color: '#22C55E' }} />
              <h3 style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#86EFAC',
                fontFamily: 'Rajdhani, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Item Details
              </h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  Item Name
                </label>
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#E0E6F0',
                  fontSize: '0.875rem'
                }}>
                  {itemData?.name || 'N/A'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  IC Code
                </label>
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#22C55E',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  fontWeight: 600
                }}>
                  {itemData?.ic || 'N/A'}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    Target Price
                  </label>
                  <div style={{
                    background: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    color: '#10B981',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    ${itemData?.targetPrice ? Number(itemData.targetPrice).toFixed(2) : '0.00'}
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    Cost
                  </label>
                  <div style={{
                    background: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    color: '#F59E0B',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}>
                    ${itemData?.cost ? Number(itemData.cost).toFixed(2) : '0.00'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sale Information Section */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(16, 185, 129, 0.08))',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1), transparent)',
              pointerEvents: 'none'
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <DollarSign style={{ width: '20px', height: '20px', color: '#10B981' }} />
              <h3 style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#86EFAC',
                fontFamily: 'Rajdhani, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Sale Information
              </h3>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Marketplace *
                </label>
                <select
                  value={formData.marketplace}
                  onChange={(e) => handleInputChange('marketplace', e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  <option value="">Select Marketplace</option>
                  <option value="eBay">eBay</option>
                  <option value="Amazon">Amazon</option>
                  <option value="Facebook Marketplace">Facebook Marketplace</option>
                  <option value="Craigslist">Craigslist</option>
                  <option value="Local Sale">Local Sale</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Sold For ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.soldFor}
                  onChange={(e) => handleInputChange('soldFor', e.target.value)}
                  placeholder="0.00"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#E0E6F0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px'
                  }}>
                    Marketplace Fees ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.marketplaceFees}
                    onChange={(e) => handleInputChange('marketplaceFees', e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(17, 24, 39, 0.8)',
                      border: '2px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '6px',
                      color: '#E0E6F0',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#E0E6F0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px'
                  }}>
                    Shipping Cost ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.shippingCost}
                    onChange={(e) => handleInputChange('shippingCost', e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(17, 24, 39, 0.8)',
                      border: '2px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '6px',
                      color: '#E0E6F0',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#E0E6F0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px'
                  }}>
                    COGS ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cogs}
                    onChange={(e) => handleInputChange('cogs', e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(17, 24, 39, 0.8)',
                      border: '2px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '6px',
                      color: '#E0E6F0',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#E0E6F0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px'
                  }}>
                    Quantity Sold
                  </label>
                  <input
                    type="number"
                    value={formData.quantitySold}
                    onChange={(e) => handleInputChange('quantitySold', e.target.value)}
                    min="1"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(17, 24, 39, 0.8)',
                      border: '2px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '6px',
                      color: '#E0E6F0',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Sale Date *
                </label>
                <input
                  type="date"
                  value={formData.saleDate}
                  onChange={(e) => handleInputChange('saleDate', e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Additional notes about the sale..."
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    resize: 'vertical',
                    fontFamily: 'system-ui, sans-serif'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(34, 197, 94, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>

              {/* Net Profit Display */}
              <div style={{
                padding: '16px',
                background: calculateNetProfit() >= 0
                  ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.2))'
                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))',
                border: calculateNetProfit() >= 0
                  ? '2px solid rgba(34, 197, 94, 0.4)'
                  : '2px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: calculateNetProfit() >= 0
                  ? '0 0 20px rgba(34, 197, 94, 0.3)'
                  : '0 0 20px rgba(239, 68, 68, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TrendingUp style={{
                    width: '20px',
                    height: '20px',
                    color: calculateNetProfit() >= 0 ? '#22C55E' : '#EF4444'
                  }} />
                  <span style={{
                    color: '#E0E6F0',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Net Profit:
                  </span>
                </div>
                <span style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: calculateNetProfit() >= 0 ? '#22C55E' : '#EF4444',
                  fontFamily: 'monospace',
                  textShadow: calculateNetProfit() >= 0
                    ? '0 0 10px rgba(34, 197, 94, 0.5)'
                    : '0 0 10px rgba(239, 68, 68, 0.5)'
                }}>
                  ${calculateNetProfit().toFixed(2)}
                </span>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: 'rgba(107, 114, 128, 0.2)',
                    border: '2px solid rgba(107, 114, 128, 0.4)',
                    borderRadius: '8px',
                    color: '#9CA3AF',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(107, 114, 128, 0.3)'
                    e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.6)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(107, 114, 128, 0.2)'
                    e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.4)'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: isSubmitting
                      ? 'rgba(34, 197, 94, 0.3)'
                      : 'linear-gradient(135deg, #22C55E, #10B981)',
                    border: '2px solid rgba(34, 197, 94, 0.6)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: 'Rajdhani, sans-serif',
                    boxShadow: isSubmitting ? 'none' : '0 0 20px rgba(34, 197, 94, 0.4)',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.boxShadow = '0 0 30px rgba(34, 197, 94, 0.6)'
                      e.currentTarget.style.transform = 'scale(1.02)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(34, 197, 94, 0.4)'
                      e.currentTarget.style.transform = 'scale(1)'
                    }
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingCart style={{ width: '16px', height: '16px' }} />
                      Mark as Sold
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
